import { useMount } from "react-use";
import { useCallback, useState } from "react";
import { PaymentMethod } from "@stripe/stripe-js";
import { tryAsyncFunc } from "globals/functions/async.ts";
import ajax from "config/ajax.ts";
import Button from "components/atom/button/Button.tsx";
import CreditCardForm from "components/layouts/form/CreditCardForm.tsx";
import ConfirmActionModal from "components/modals/ConfirmActionModal.tsx";
import { useAppDispatch, useAppSelector } from "config/store";
import { authActions } from "config/store/modules/auth.ts";
import PaymentMethodCard from "components/layouts/card/PaymentMethodCard.tsx";
import { useTranslation } from "react-i18next";
import { USER_PLAN_TYPES } from "globals/constants.ts";
import ActionBlockedModal from "components/modals/ActionBlockedModal.tsx";
import { useNavigate } from "react-router-dom";

function PaymentMethodForm() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUser = useAppSelector(state => state.auth.user);
  const paymentMethods = useAppSelector(state => state.auth.paymentMethods);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [showCreatePaymentMethod, setShowCreatePaymentMethod] = useState<boolean>(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState<boolean>(false);
  const [changePlanBeforeDeleteModal, setChangePlanBeforeDeleteModal] = useState<boolean>(false);


  useMount(async () => {
    dispatch(authActions.getMyPaymentMethods());
  });

  const addPaymentMethod = useCallback(() => {
    setShowCreatePaymentMethod(true);
  }, []);

  const onWantedDeletePaymentMethod = useCallback((paymentMethod: PaymentMethod) => {
    setSelectedPaymentMethod(paymentMethod);

    if (currentUser?.planType === USER_PLAN_TYPES.UNIT || currentUser?.planType === USER_PLAN_TYPES.COMPLETE_MONTHLY || currentUser?.planType === USER_PLAN_TYPES.COMPLETE_YEARLY) {
      setChangePlanBeforeDeleteModal(true);
      return;
    }

    setConfirmDeleteModal(true);
  }, [currentUser]);

  const removePaymentMethod = useCallback(async () => {
    if (!selectedPaymentMethod) return;

    setConfirmDeleteModal(false);

    const [_, err] = await tryAsyncFunc(ajax.delete(`/connected/me/payment-methods/${selectedPaymentMethod.id}`));

    if (err) {
      return;
    }

    dispatch(authActions.getMyPaymentMethods());
  }, [dispatch, selectedPaymentMethod]);

  const onCreatedNewPaymentMethod = useCallback(async () => {
    setShowCreatePaymentMethod(false);
    dispatch(authActions.getMyPaymentMethods());
  }, [dispatch]);

  return (
    <div>
      <div className="space-y-2">
        {paymentMethods.map((paymentMethod) => (
          <PaymentMethodCard
            key={paymentMethod.id}
            paymentMethod={paymentMethod}
            onDelete={() => onWantedDeletePaymentMethod(paymentMethod)} />
        ))}
      </div>

      <div>
        {showCreatePaymentMethod && (
          <div>
            <CreditCardForm
              onFinish={onCreatedNewPaymentMethod}
              onCancel={() => setShowCreatePaymentMethod(false)} />
          </div>
        )}
      </div>

      {!showCreatePaymentMethod && (
        <div className="mt-6 flex flex-row items-center justify-center">
          <Button smaller style="normal" onClick={addPaymentMethod}>
            {paymentMethods.length > 0 ? (
              <span>
                {t("forms.replacePaymentMethod")}
              </span>
            ) : (
              <span>
                {t("forms.addPaymentMethod")}
              </span>
            )}

          </Button>
        </div>
      )}

      <ConfirmActionModal
        isOpen={confirmDeleteModal}
        onClose={() => setConfirmDeleteModal(false)}
        onAccept={removePaymentMethod}
      >
        {selectedPaymentMethod && (
          <p className="text-center text-gray-400">
            {t("forms.deletePaymentMethod")}
          </p>
        )}

      </ConfirmActionModal>

      <ActionBlockedModal isOpen={changePlanBeforeDeleteModal}
                          onClose={() => setChangePlanBeforeDeleteModal(false)}
      >

        <p className="text-center text-gray-400">
          {t("plans.pleaseCancelPlanBeforeDeletePaymentMethod")}
        </p>


        <Button className="mt-12 block w-full" onClick={() => navigate("/profile/me/plans")}>
          {t("plans.seeAllPlans")}
        </Button>
      </ActionBlockedModal>
    </div>
  );
}

export default PaymentMethodForm;
